import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Link from '@atlaskit/link';

import type { FlagsLike } from './useMarkdownModeActions.ts';

const i18n = defineMessages({
	convertToMarkdownError: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-markdown-error',
		description: 'Error flag shown when converting a live page to markdown mode fails',
		defaultMessage: 'Failed to convert to markdown',
	},
	convertToRichTextError: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-rich-text-error',
		description:
			'Error flag shown when converting a live page from markdown mode back to rich text fails',
		defaultMessage: 'Failed to convert to rich text',
	},
	convertToMarkdownSuccessTitle: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-markdown-success.title',
		description:
			'Title for the flag shown when the current user has converted a live doc to a markdown mode page',
		defaultMessage: 'You’ve converted to markdown',
	},
	convertToMarkdownSuccessBody: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-markdown-success.body',
		description:
			'Body for the flag shown after a live doc is converted to a markdown mode page; explains that further edits to this doc are now made in markdown',
		defaultMessage: 'Edits to this doc are now made in markdown.',
	},
	findOutMoreAboutMarkdown: {
		id: 'content-markdown-mode.use-markdown-mode-actions.find-out-more.markdown',
		description:
			'Link on the convert-to-markdown success flag that opens documentation about markdown mode',
		defaultMessage: 'Find out more about markdown',
	},
	convertToRichTextSuccessTitle: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-rich-text-success.title',
		description:
			'Title for the flag shown when the current user has converted a markdown mode page back to a live doc',
		defaultMessage: 'You’ve converted to a live doc',
	},
	convertToRichTextSuccessBody: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-rich-text-success.body',
		description:
			'Body for the flag shown after a markdown mode page is converted to a live doc; explains updates are now real-time',
		defaultMessage: 'Updates to this doc will now be instant and visible in real time.',
	},
	findOutMoreAboutLiveDocs: {
		id: 'content-markdown-mode.use-markdown-mode-actions.find-out-more.live-docs',
		description:
			'Link on the convert-to-live-doc success flag that opens documentation about live docs',
		defaultMessage: 'Find out more about live docs',
	},
});

const LIVE_DOCS_LEARN_MORE_URL =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';
const MARKDOWN_LEARN_MORE_URL =
	'https://support.atlassian.com/confluence-cloud/docs/keyboard-shortcuts-markdown-and-autocomplete/';

// Matches the page → live-doc convert success flag.
const CONVERT_SUCCESS_FLAG_AUTO_CLOSE_MS = 11000;

export type ConvertDirection = 'toMarkdown' | 'toRichText';

export type MarkdownConvertFlags = {
	/** Rich success flag with Find-out-more action, auto-dismissed. */
	showSuccess: (direction: ConvertDirection) => void;
	/** Error flag with the message matching the conversion direction. */
	showError: (direction: ConvertDirection) => void;
};

/**
 * Notification rendering for markdown ↔ live-doc conversion. Lives
 * apart from `useMarkdownModeActions` so the orchestrator stays
 * JSX-free. Each flags method is independently optional — unsupported
 * notifications silently no-op.
 */
export const useMarkdownConvertFlags = (flags: FlagsLike): MarkdownConvertFlags => {
	const intl = useIntl();

	return useMemo<MarkdownConvertFlags>(
		() => ({
			showError: (direction) => {
				if (!flags.showErrorFlag) {
					return;
				}
				const message =
					direction === 'toMarkdown' ? i18n.convertToMarkdownError : i18n.convertToRichTextError;
				void flags.showErrorFlag({ title: intl.formatMessage(message) });
			},
			showSuccess: (direction) => {
				if (!flags.showFlag) {
					return;
				}
				const isToMarkdown = direction === 'toMarkdown';
				const flagId = isToMarkdown
					? 'content-markdown-mode-convert-to-markdown-flag'
					: 'content-markdown-mode-convert-to-rich-text-flag';
				const learnMoreUrl = isToMarkdown ? MARKDOWN_LEARN_MORE_URL : LIVE_DOCS_LEARN_MORE_URL;
				const titleMessage = isToMarkdown
					? i18n.convertToMarkdownSuccessTitle
					: i18n.convertToRichTextSuccessTitle;
				const bodyMessage = isToMarkdown
					? i18n.convertToMarkdownSuccessBody
					: i18n.convertToRichTextSuccessBody;
				const learnMoreMessage = isToMarkdown
					? i18n.findOutMoreAboutMarkdown
					: i18n.findOutMoreAboutLiveDocs;

				void flags.showFlag({
					id: flagId,
					type: 'success',
					title: intl.formatMessage(titleMessage),
					description: intl.formatMessage(bodyMessage),
					// Survive the editor-unmount `hideAllFlags()` during the
					// conversion remount — we dismiss manually below.
					close: 'never',
					actions: [
						{
							// Real anchor via `@atlaskit/link` — browser owns
							// navigation. `rel="noopener noreferrer"` prevents
							// tabnabbing (canonical Confluence pattern, see
							// `editor-flags/buildEditorFlagMessage.tsx`).
							content: (
								<Link
									href={learnMoreUrl}
									target="_blank"
									rel="noopener noreferrer"
									appearance="subtle"
								>
									{intl.formatMessage(learnMoreMessage)}
								</Link>
							),
							onClick: () => {},
						},
					],
				});
				setTimeout(() => flags.hideFlag?.(flagId), CONVERT_SUCCESS_FLAG_AUTO_CLOSE_MS);
			},
		}),
		[flags, intl],
	);
};
